import type { HeatmapWidgetStateParams } from '../../types';
import type { HeatmapWidgetAxisParams } from '../types';
import { AnalysisType } from '../types';

import type { AxisKey, AxisParamsByKey } from './settingsStoreConfig';
import { BASE_AXIS_NAMES, createAxisParamsFromConfig, METRIC_NAMES } from './settingsStoreConfig';

export function isUnitValid(unit: HeatmapWidgetAxisParams['unit']) {
  return unit && (unit.id === null || Number.isInteger(unit.id)) && typeof unit.abbreviation === 'string';
}

export function isAnalysisTypeValid(type: AnalysisType) {
  return typeof type === 'number' && Object.values(AnalysisType).includes(type);
}

export function restoreAxisParams(state: Partial<HeatmapWidgetStateParams>) {
  const axes = createAxisParamsFromConfig();
  for (const name of BASE_AXIS_NAMES) {
    const key = `${name}AxisParams` as const;
    axes[key] = {
      ...axes[key],
      ...state[`${name}Range`],
      ...state[`${name}Step`],
    };
  }
  for (const name of ['wob', 'dpress', ...METRIC_NAMES] as const) {
    const unit = state[`${name}Unit`];
    if (unit && isUnitValid(unit)) {
      axes[`${name}AxisParams`].unit = { ...unit };
    }
  }
  for (const name of METRIC_NAMES) {
    const type = state[`${name}AnalysisType`];
    if (type !== undefined && isAnalysisTypeValid(type)) {
      axes[`${name}AxisParams`].analysisType = type;
    }
  }
  return axes;
}

export function serializeAxisParams(
  axes: AxisParamsByKey,
  unitAxisKeys?: readonly AxisKey[],
): Partial<HeatmapWidgetStateParams> {
  const state: Partial<HeatmapWidgetStateParams> = {};
  for (const name of BASE_AXIS_NAMES) {
    const { min, max, step } = axes[`${name}AxisParams`];
    state[`${name}Range`] = { min, max };
    state[`${name}Step`] = { step };
  }
  for (const name of ['wob', 'dpress', ...METRIC_NAMES] as const) {
    if (unitAxisKeys && !unitAxisKeys.includes(`${name}AxisParams`)) {
      continue;
    }
    const { unit } = axes[`${name}AxisParams`];
    state[`${name}Unit`] = unit.id === null ? null : { id: unit.id, abbreviation: unit.abbreviation };
  }
  for (const name of METRIC_NAMES) {
    state[`${name}AnalysisType`] = axes[`${name}AxisParams`].analysisType;
  }
  return state;
}
